import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'

import { Subtitle1 } from '@src/elements/typography/typography'
import { Select } from '@src/elements/select/select'
import { Input } from '@src/elements/input/input'
import { Button } from '@src/elements/button/button'
import { OptionGroup } from '@src/elements/select/option-group'
import { Option } from '@src/elements/select/option'
import { AVAILABLE_LOCALES, Languages } from '@src/util/constants'
import {
  isEmailValid,
  isFieldFilled,
  isUsernameValid,
  validateRequiredField
} from '@src/util/validation'
import Member from '@src/data/member'
import { EditMemberDto } from '@src/dto/edit-member-dto'
import EventBus from '@src/lib/event_bus'
import { DatePicker } from '@src/elements/input/date-picker'
import { ContentBox } from '@src/elements/content-box/content-box'
import { InputFootnote } from '@src/elements/input/input-footnote'
import { MemberService } from '@src/service/member'
import { MemberProfile } from '@src/data/member-profile'
import { convertToLocalDate, convertToYYYYMMDD } from '@src/util/date-format'
import SessionManager from '@src/lib/session_manager'
import Cookies from '@src/lib/cookies'
import { getGenderName } from '@src/util/functions'

class ErrorResponse {
  errors: {
    base: string
    username: string[]
  }

  constructor (attributes?) {
    Object.assign(this, attributes)
  }
}

@Component({
  computed: {
    initialMember () {
      return this.$store.state.selectedMember || this.$store.state.memberProfile
    },
    platformLocale () {
      return this.$store.state.i18n.locale
    }
  }
})
export default class PersonalInformation extends Vue {

  protected initialMember: Member | MemberProfile
  protected member: EditMemberDto = new EditMemberDto()
  protected fieldErrors: string[] = []
  protected platformLocale: string
  protected service: MemberService = new MemberService()
  protected emailRequirements: string

  constructor () {
    super()
    this.emailRequirements = 'PAGE.MEMBERS.ACCOUNT.EMAIL_REQUIREMENTS'
  }

  beforeMount () {
    this.setInitialValues()
  }

  save () {
    this.autofillUsername()
    this.validateForm()

    if (this.fieldErrors.length === 0) {
      let memberToSave = new EditMemberDto(this.member)
      if (memberToSave.birthdate) {
        memberToSave.birthdate = convertToYYYYMMDD(memberToSave.birthdate)
      }
      return this.editProfile(memberToSave)
    }
    this.scrollTop()
  }

  editProfile (member: EditMemberDto) {
    this.service.updatePersonalInformation(member, this.initialMember.id)
      .then(() => {
        this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.ACCOUNT.PERSONAL_INFORMATION_SAVED'), fadeOut: true })
        EventBus.trigger('reloadSelectedMember')
      })
      .catch(error => this.setErrors(error.status, new ErrorResponse(error.errorData)))
  }

  setErrors (statusCode, errorResponse) {
    switch (true) {
      case statusCode === 422 && errorResponse.errors && errorResponse.errors.base && errorResponse.errors.base.includes('email IS_TAKEN'):
        this.fieldErrors.push('email_duplicate')
        break
      case statusCode === 422 && errorResponse.errors && errorResponse.errors.email && errorResponse.errors.email.length > 0 :
        this.fieldErrors.push('email')
        break
      case statusCode === 422 && errorResponse.errors && errorResponse.errors.username && errorResponse.errors.username.length > 0 :
        this.fieldErrors.push('username_duplicate')
        break
      case statusCode === 422 && errorResponse.errors && errorResponse.errors.base && errorResponse.errors.base.includes('username'):
        this.fieldErrors.push('username')
        break
    }
  }

  validateForm () {
    let fieldErrors: string[] = []
    // Username is now optional, only validate if filled
    if (isFieldFilled(this.member.username)) {
      if (this.member.username.length < 5) {
        fieldErrors.push('username_too_short')
      }
      if (!isUsernameValid(this.member.username)) {
        fieldErrors.push('username')
      }
    }
    if (validateRequiredField(this.member.firstName)) {
      fieldErrors.push('firstName')
    }
    if (validateRequiredField(this.member.lastName)) {
      fieldErrors.push('lastName')
    }
    if (isFieldFilled(this.member.email)) {
      if (!isEmailValid(this.member.email!)) {
        fieldErrors.push('email')
      }
    }
    // Display name is now optional, only validate if filled
    if (isFieldFilled(this.member.displayName)) {
      if (this.member.displayName.trim().length < 2) {
        fieldErrors.push('displayName_too_short')
      }
    }
    this.fieldErrors = fieldErrors
  }

  autofillUsername () {
    // autocomplete username with the qr code if empty
    if (!isFieldFilled(this.member.username)) {
      this.member.username = this.initialMember.codeSerial || ''
    }
    // autocomplete displayName with the qr code if empty
    if (!isFieldFilled(this.member.displayName)) {
      this.member.displayName = this.initialMember.codeSerial || ''
    }
  }

  setInitialValues () {
    this.member = new EditMemberDto(this.initialMember)

    // setup default locales in case these are not set
    if (!this.member.displayLocale || this.member.displayLocale === '') {
      this.member.displayLocale = this.platformLocale
    }
    if (!this.member.locale || this.member.locale === '') {
      this.member.locale = this.platformLocale
    }
    if (this.member.birthdate) {
      this.member.birthdate = convertToLocalDate(this.member.birthdate)
    }
    console.log(this.member)
  }
  reset () {
    this.setInitialValues()
    this.$children.forEach(child => child.$emit('resetValue'))
    this.$forceUpdate()
  }

  changeWebPlatformLanguage (value: string) {
    if (!SessionManager.Instance.isManager()) {
      this.$store.dispatch('switchLanguage', value)
      Cookies.set('Locale', value, { expires: '5Y' })
    }
    this.member.locale = value
    this.$forceUpdate()
  }

  changeStationLanguage (value: string) {
    this.member.displayLocale = value
    this.$forceUpdate()
  }

  setUsername (value) {
    this.member.username = value.replace(/\s+/g, '')
  }

  removeProfileImage () {
    this.member.avatarOriginalUrl = ''
    this.member.avatar = null
    this.$forceUpdate()
  }

  scrollTop () {
    let container = document.querySelector('.scroll-content')
    if (container) {
      let firstErrorInput = container.querySelector('.error')
      if (firstErrorInput) {
        container.scrollTop = firstErrorInput.getBoundingClientRect().top
      }
    }
  }

  render (h: CreateElement) {
    let stationLanguages = Object.keys(Languages).map((language) => {
      return <Option type='single-option' class='short' value={Languages[language]} label={this.$t(`LANGUAGES.${Languages[language]}`)}
                     selected={this.member.displayLocale === Languages[language]} />
    })

    let webPlatformLanguages = AVAILABLE_LOCALES.map((language) => {
      return <Option type='single-option' class='short' value={language} label={this.$t(`LANGUAGES.${language}`)}
                     selected={this.member.locale === language} />
    })

    return <div class='personal-information' direction='column'>
      <Subtitle1>{this.$t('PAGE.MEMBERS.ACCOUNT.PERSONAL_INFORMATION')}</Subtitle1>

      <ContentBox>
        <div slot='content'>
          <div class='row'>
            <Input ref='firstName' class={`short ${this.fieldErrors.indexOf('firstName') >= 0 ? 'error' : ''}`} value={this.member.firstName}
                   label={`${this.$t('FIRST_NAME')}*`} valueChanged={(value) => this.member.firstName = value} />
            <Input ref='lastName' class={`short ${this.fieldErrors.indexOf('lastName') >= 0 ? 'error' : ''}`} value={this.member.lastName}
                   label={`${this.$t('LAST_NAME')}*`} valueChanged={(value) => this.member.lastName = value} />
          </div>

          <div class='row'>
            <div class='short'>
              <Input ref='username' class={this.fieldErrors.find((u) => u.includes('username')) ? 'error' : ''}
                     value={this.member.username} valueChanged={this.setUsername} label={this.$t('USERNAME')} />
              <InputFootnote>&#8226; {this.$t('PAGE.MEMBERS.CREATE.USERNAME_AUTOFILL_HINT') || 'Will be auto-filled with QR code if empty'}</InputFootnote>
              <InputFootnote class='error'>{this.fieldErrors.includes('username_duplicate') ? this.$t('PAGE.MEMBERS.CREATE.ERROR.DUPLICATE_USERNAME') : null}</InputFootnote>
              {this.fieldErrors.includes('username_too_short') && this.member.username ? <InputFootnote class='error'>&#8226; {this.$t('PAGE.MEMBERS.ACCOUNT.USERNAME_TOO_SHORT')}</InputFootnote> : ''}
              {this.fieldErrors.includes('username') && this.member.username ? <InputFootnote class='error'>&#8226; {this.$t('PAGE.MEMBERS.ACCOUNT.USERNAME_CHARACTERS_ALLOWED')}</InputFootnote> : '' }
            </div>
            <div class='short'>
              <Input ref='displayName' class={`${this.fieldErrors.indexOf('displayName') >= 0 || this.fieldErrors.indexOf('displayName_too_short') >= 0 ? 'error' : ''}`} value={this.member.displayName}
                     valueChanged={(value) => this.member.displayName = value} label={this.$t('PAGE.MEMBERS.ACCOUNT.DISPLAY_NAME')} />
              <InputFootnote>&#8226; {this.$t('PAGE.MEMBERS.CREATE.DISPLAYNAME_AUTOFILL_HINT') || 'Will be auto-filled with QR code if empty'}</InputFootnote>
              <InputFootnote class='error'>{this.fieldErrors.includes('displayName_too_short') && this.member.displayName ? this.$t('PAGE.MEMBERS.ACCOUNT.DISPLAY_NAME_TOO_SHORT') : null}</InputFootnote>
            </div>
          </div>

          <div class='row'>
            <div class='short'>
              <Input ref='email' class={`${this.fieldErrors.indexOf('email') >= 0 || this.fieldErrors.indexOf('email_duplicate') >= 0 ? 'error' : ''}`} value={this.member.email}
                     valueChanged={(value) => this.member.email = value} label={this.$t('PAGE.MEMBERS.ACCOUNT.EMAIL_ADDRESS')} />
              <InputFootnote class='error'>{this.fieldErrors.includes('email') ? this.$t('PAGE.MEMBERS.CREATE.ERROR.INVALID_EMAIL.MESSAGE') : null}</InputFootnote>
              <InputFootnote class='error'>{this.fieldErrors.includes('email_duplicate') ? this.$t('PAGE.MEMBERS.CREATE.ERROR.DUPLICATE_EMAIL.MESSAGE') : null}</InputFootnote>
              <InputFootnote>{this.$t(this.emailRequirements)}</InputFootnote>
            </div>
            <Input ref='contactNumber' value={this.member.contactNumber} valueChanged={(value) => this.member.contactNumber = value}
                   label={this.$t('PAGE.MEMBERS.ACCOUNT.CONTACT_NUMBER')} class='short' />
          </div>

          <div class='row'>
            <Input ref='addressLine1' value={this.member.addressLine1} valueChanged={(value) => this.member.addressLine1 = value}
                   label={`${this.$t('PAGE.CLUB.CLUB_INFO.ADDRESS_LINE')} 1`} />
          </div>

          <div class='row'>
            <Input ref='addressLine2' value={this.member.addressLine2} valueChanged={(value) => this.member.addressLine2 = value}
                   label={`${this.$t('PAGE.CLUB.CLUB_INFO.ADDRESS_LINE')} 2`} />
          </div>

          <div class='row'>
            <Input ref='addressPostcode' class='short' value={this.member.addressPostcode}
                   label={this.$t('PAGE.MEMBERS.ACCOUNT.POSTAL_CODE')} valueChanged={(value) => this.member.addressPostcode = value} />
            <Input ref='addressCity' class='short' value={this.member.addressTownCity}
                   label={this.$t('PAGE.CLUB.CLUB_INFO.CITY')} valueChanged={(value) => this.member.addressTownCity = value} />
          </div>

          <div class='row'>
            <Input ref='addressCountry' class='short' value={this.member.addressCountry} valueChanged={(value) => this.member.addressCountry = value}
                   label={this.$t('PAGE.CLUB.CLUB_INFO.COUNTRY')} />
          </div>

          <div class='row'>
            <Select ref='gender' class='short' valueChanged={(value: string) => this.member.gender = value}
                    value={this.$t(getGenderName(this.member.gender))}
                    label={this.$t('PAGE.MEMBERS.GENDER.TITLE')}>
              <Option type='single-option' value={'m'} label={this.$t('PAGE.MEMBERS.GENDER.MALE')} selected={this.member.gender === 'm'} />
              <Option type='single-option' value={'f'} label={this.$t('PAGE.MEMBERS.GENDER.FEMALE')} selected={this.member.gender === 'f'} />
              <Option type='single-option' value={'d'} label={this.$t('PAGE.MEMBERS.GENDER.DIVERSE')} selected={this.member.gender === 'd'} />
            </Select>
            <DatePicker ref='birthdate' type='date' class='short' value={this.member.birthdate} label={this.$t('BIRTHDATE')}
                        change={(value: string) => this.member.birthdate = value} allowFutureDate={false} />
          </div>

          <div class='row'>
            <Select class='short' ref='platformLanguage' valueChanged={this.changeWebPlatformLanguage} label={this.$t('PAGE.MEMBERS.ACCOUNT.PLATFORM_LANGUAGE')}
                    value={this.member.locale ? this.$t(`LANGUAGES.${this.member.locale}`) : null}>
              <OptionGroup>
                {webPlatformLanguages}
              </OptionGroup>
            </Select>
            <Select class='short' ref='stationLanguage' valueChanged={this.changeStationLanguage} label={this.$t('PAGE.MEMBERS.ACCOUNT.STATION_LANGUAGE')}
                    value={this.member.displayLocale ? this.$t(`LANGUAGES.${this.member.displayLocale}`) : null}>
              <OptionGroup>
                {stationLanguages}
              </OptionGroup>
            </Select>
          </div>

          <div class='row'>
            <Input ref='emergencyPerson' class='short' value={this.member.emergencyContactName} valueChanged={(value) => this.member.emergencyContactName = value}
                   label={this.$t('PAGE.MEMBERS.ACCOUNT.EMERGENCY_CONTACT')} />
            <Input ref='emergencyRelationship' class='short' value={this.member.emergencyContactRelationship} valueChanged={(value) => this.member.emergencyContactRelationship = value}
                   label={this.$t('PAGE.MEMBERS.ACCOUNT.EMERGENCY_CONTACT_RELATIONSHIP')} />
          </div>

          <div class='row'>
            <Input ref='emergencyContactNumber' class='short' value={this.member.emergencyContactNumber} valueChanged={(value) => this.member.emergencyContactNumber = value}
                   label={this.$t('PAGE.MEMBERS.ACCOUNT.EMERGENCY_CONTACT_NUMBER')} />
            {SessionManager.Instance.isOwner() && this.member.clubRoleType !== 'member' ? <Select ref='club-role' class='short' valueChanged={(value: string) => this.member.clubRoleType = value}
                    value={this.$t(`PAGE.MEMBERS.ROLE.${this.member.clubRoleType.toUpperCase()}`)}
                    label={this.$t('PAGE.MEMBERS.ROLE.SELECT')}>
              <Option type='single-option' value={'assistant'} label={this.$t('PAGE.MEMBERS.ROLE.ASSISTANT')} selected={this.member.clubRoleType === 'assistant'} />
              <Option type='single-option' value={'trainer'} label={this.$t('PAGE.MEMBERS.ROLE.TRAINER')} selected={this.member.clubRoleType === 'trainer'} />
              <Option type='single-option' value={'therapist'} label={this.$t('PAGE.MEMBERS.ROLE.THERAPIST')} selected={this.member.clubRoleType === 'therapist'} />
            </Select> : null}
            {SessionManager.Instance.getRole() === 'member' && (
              <div class='profile-upload'>
                <img src={this.member.avatarOriginalUrl || '/assets/img/profile-picture.png'} alt='profile-image' class='profile-image'/>
                <input type='file' id='profileImage' accept='.jpg,.jpeg,.png' style='display: none;'
                       onChange={async (e) => {
                         const file = e.target.files[0]
                         if (file) {
                           const reader = new FileReader()
                           reader.onloadend = () => {
                             this.member.avatar = {
                               content: (reader.result as string).split(',')[1],
                               filename: file.name,
                               content_type: file.type
                             }
                             this.member.avatarOriginalUrl = URL.createObjectURL(file)
                             this.$forceUpdate()
                           }
                           reader.readAsDataURL(file)
                         }
                       }}/>
                <div id='imagine-info'>
                  <label for='profileImage' class='choose-file-button'>{(this.$t('PAGE.MEMBERS.ACCOUNT.CHOOSE_FILE') as string).toUpperCase()}</label>
                  <small class='file-info'>{this.$t('PAGE.MEMBERS.ACCOUNT.FILE_INFO')}</small>
                </div>
                {this.member.avatarOriginalUrl && this.member.avatarOriginalUrl !== '' ? (
                  <span class='uploaded-file-name' onClick={() => this.removeProfileImage()}>
                    {this.$t('PAGE.MEMBERS.ACCOUNT.REMOVE_PHOTO')}
                                <span class='remove-file'>&#x2715;</span>
                  </span>
                ) : (
                  <span id='no-uploaded-file-name'>
                    {this.$t('PAGE.MEMBERS.ACCOUNT.NO_FILE_SELECTED')}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div class='btn-row' slot='bottom'>
          <Button ref='resetBtn' class='reset-btn' type='outline' label={this.$t('RESET')} click={this.reset} />
          <Button ref='saveBtn' class='save-btn' type='elevated' label={this.$t('PAGE.MEMBERS.ACCOUNT.UPDATE')} click={this.save} />
        </div>
      </ContentBox>
    </div>
  }
}
